import { useAuth } from 'src/auth/AuthContext';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableSortLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  TablePagination,
  Avatar,
  InputAdornment
} from '@mui/material';
import {
  ExpandMore,
  TrendingUp,
  TrendingDown,
  People,
  LocalCafe,
  CardGiftcard,
  Refresh,
  FilterList,
  Clear,
  Search
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import moment from 'moment';
import {
  useDailyStamps,
  useInactiveMerchants,
  useMerchantsWithStats,
  useOverviewStats,
  useRecentRewards,
  useRecentStamps,
  useStampsByStore,
  useUserGrowth,
} from 'src/queries/stats';
import MerchantMap from '../../../components/MerchantMap';

type TimeFilter = 'today' | 'this_week' | 'this_month' | 'this_year' | 'all_time' | 'custom';
type Order = 'asc' | 'desc';
type MerchantSortField = 'id' | 'name' | 'type' | 'subscriptionStatus' | 'subscriptionToken' | 'billingDate' | 'dateCreated' | 'dateUpdated' | 'totalStamps';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
const INACTIVITY_COLORS: Record<string, string> = {
  '1 Day': '#4CAF50',
  '2 Days': '#8BC34A',
  '3 Days': '#CDDC39',
  '4-7 Days': '#FFC107',
  '15+ Days': '#FF5722',
  'Never had purchases': '#9E9E9E'
};

// Helper function to safely handle change percent values
const formatChangePercent = (value: number): { display: string; isPositive: boolean; isValid: boolean } => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return { display: 'N/A', isPositive: true, isValid: false };
  }
  const clamped = Math.max(-999, Math.min(999, value));
  const isPositive = clamped >= 0;
  return { 
    display: `${Math.abs(Math.round(clamped))}%`, 
    isPositive, 
    isValid: true 
  };
};

const getDateRange = (filter: TimeFilter): { start: string; end: string } => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  switch (filter) {
    case 'today':
      return { start: todayStr, end: todayStr };
    case 'this_week': {
      const start = new Date(today);
      const day = start.getDay() || 7;
      start.setDate(start.getDate() - day + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0]
      };
    }
    case 'this_month': {
      const firstDay = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      return { start: firstDay, end: todayStr };
    }
    case 'this_year': {
      const firstDay = `${today.getFullYear()}-01-01`;
      return { start: firstDay, end: todayStr };
    }
    case 'all_time':
    default:
      return { start: '', end: '' };
  }
};


const StatsAdminPage: React.FC = () => {
  // Data now comes from queries; only the filter/UI state is local.

  // Daily stamps filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Stamps by store filter state
  const [storeTimeFilter, setStoreTimeFilter] = useState<TimeFilter>('all_time');
  const [storeCustomStartDate, setStoreCustomStartDate] = useState('');
  const [storeCustomEndDate, setStoreCustomEndDate] = useState('');

  /**
   * The range actually being queried, kept separate from the two date inputs.
   * A preset filter applies immediately; a custom range applies only when the
   * user presses Search, which is why the inputs cannot drive the query key
   * directly.
   */
  const [dailyRange, setDailyRange] = useState(() => getDateRange('this_month'));
  const [storeRange, setStoreRange] = useState(() => getDateRange('all_time'));

  // Inactive merchants filter state
  const [inactivityFilter, setInactivityFilter] = useState<string[]>([]);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<string[]>([]);

  // Merchants table state
  const [merchantsPage, setMerchantsPage] = useState(0);
  const [merchantsRowsPerPage, setMerchantsRowsPerPage] = useState(25);
  const [merchantSortField, setMerchantSortField] = useState<MerchantSortField>('id');
  const [merchantSortOrder, setMerchantSortOrder] = useState<Order>('asc');
  const [merchantSearchToken, setMerchantSearchToken] = useState('');
  const [merchantStatusFilter, setMerchantStatusFilter] = useState<string[]>([]);
  const [merchantTypeFilter, setMerchantTypeFilter] = useState<string[]>([]);

  // Accordion state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    dailyStamps: true,
    stampsByStore: true,
    userGrowth: true,
    merchantMap: true,
    inactiveMerchants: true,
    recentActivity: true,
    merchantsTable: true
  });

  const user = useAuth();

  // Picking a preset moves both the visible inputs and the queried range.
  useEffect(() => {
    if (timeFilter !== 'custom') {
      const { start, end } = getDateRange(timeFilter);
      setCustomStartDate(start);
      setCustomEndDate(end);
      setDailyRange({ start, end });
    }
  }, [timeFilter]);

  useEffect(() => {
    if (storeTimeFilter !== 'custom') {
      const { start, end } = getDateRange(storeTimeFilter);
      setStoreCustomStartDate(start);
      setStoreCustomEndDate(end);
      setStoreRange({ start, end });
    }
  }, [storeTimeFilter]);

  // Today's stamps, recomputed once per render rather than captured at fetch time.
  const todayIso = new Date().toISOString().split('T')[0];

  const overview = useOverviewStats();
  const growth = useUserGrowth();
  const inactive = useInactiveMerchants();
  const merchants = useMerchantsWithStats();
  const stamps = useRecentStamps(50, todayIso);
  const rewards = useRecentRewards(50);
  const daily = useDailyStamps(dailyRange.start || undefined, dailyRange.end || undefined);
  const byStore = useStampsByStore(storeRange.start || undefined, storeRange.end || undefined);

  const overviewStats = overview.data ?? null;
  const userGrowth = growth.data ?? [];
  const inactiveMerchants = inactive.data ?? [];
  const merchantsWithStats = merchants.data ?? [];
  const recentStamps = stamps.data ?? [];
  const recentRewards = rewards.data ?? [];
  const dailyStamps = daily.data ?? [];
  const stampsByStore = byStore.data ?? [];

  const panels = [overview, growth, inactive, merchants, stamps, rewards, daily, byStore];
  // `isPending` means "no data yet" — using it rather than `isFetching` keeps the
  // panels on screen while a background refresh runs.
  const isLoading = panels.some((panel) => panel.isPending);
  const error = panels.some((panel) => panel.isError)
    ? 'Failed to load statistics. Please try again later.'
    : null;

  const handleCustomDateSearch = () => {
    if (customStartDate && customEndDate) {
      setDailyRange({ start: customStartDate, end: customEndDate });
    }
  };

  const handleStoreCustomDateSearch = () => {
    if (storeCustomStartDate && storeCustomEndDate) {
      setStoreRange({ start: storeCustomStartDate, end: storeCustomEndDate });
    }
  };

  /**
   * Refetches every panel. This used to clear a hand-rolled Map cache first;
   * `refetch()` goes to the network regardless of staleness, so the explicit
   * cache-busting step is gone.
   */
  const handleRefresh = () => {
    panels.forEach((panel) => panel.refetch());
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleMerchantSort = (field: MerchantSortField) => {
    const isAsc = merchantSortField === field && merchantSortOrder === 'asc';
    setMerchantSortOrder(isAsc ? 'desc' : 'asc');
    setMerchantSortField(field);
  };


  // Memoized computed values
  const inactivityBucketCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    inactiveMerchants.forEach(m => {
      counts[m.inactivityBucket] = (counts[m.inactivityBucket] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [inactiveMerchants]);

  const filteredInactiveMerchants = useMemo(() => {
    return inactiveMerchants.filter(m => {
      const bucketMatch = inactivityFilter.length === 0 || inactivityFilter.includes(m.inactivityBucket);
      const statusMatch = subscriptionStatusFilter.length === 0 || subscriptionStatusFilter.includes(m.subscriptionStatus);
      return bucketMatch && statusMatch;
    });
  }, [inactiveMerchants, inactivityFilter, subscriptionStatusFilter]);

  const uniqueInactivityBuckets = useMemo(() => 
    Array.from(new Set(inactiveMerchants.map(m => m.inactivityBucket))).sort(), 
    [inactiveMerchants]
  );

  const uniqueSubscriptionStatuses = useMemo(() => 
    Array.from(new Set(inactiveMerchants.map(m => m.subscriptionStatus))).sort(), 
    [inactiveMerchants]
  );

  const uniqueMerchantStatuses = useMemo(() => 
    Array.from(new Set(merchantsWithStats.map(m => m.subscriptionStatus).filter(Boolean))).sort() as string[], 
    [merchantsWithStats]
  );

  const uniqueMerchantTypes = useMemo(() => 
    Array.from(new Set(merchantsWithStats.map(m => m.type))).sort(), 
    [merchantsWithStats]
  );

  const filteredAndSortedMerchants = useMemo(() => {
    const filtered = merchantsWithStats.filter(m => {
      const statusMatch = merchantStatusFilter.length === 0 || 
        (m.subscriptionStatus && merchantStatusFilter.includes(m.subscriptionStatus));
      const typeMatch = merchantTypeFilter.length === 0 || merchantTypeFilter.includes(m.type);
      const tokenMatch = !merchantSearchToken || 
        (m.subscriptionToken && m.subscriptionToken.toLowerCase().includes(merchantSearchToken.toLowerCase())) ||
        m.name.toLowerCase().includes(merchantSearchToken.toLowerCase());
      return statusMatch && typeMatch && tokenMatch;
    });

    return filtered.sort((a, b) => {
      let aVal: any = a[merchantSortField];
      let bVal: any = b[merchantSortField];

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Handle date fields
      if (merchantSortField === 'billingDate' || merchantSortField === 'dateCreated' || merchantSortField === 'dateUpdated') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      // Handle numeric fields
      if (merchantSortField === 'id' || merchantSortField === 'totalStamps') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      // String comparison for other fields
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return merchantSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return merchantSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [merchantsWithStats, merchantStatusFilter, merchantTypeFilter, merchantSearchToken, merchantSortField, merchantSortOrder]);


  // Loading states
  if (isLoading && !overviewStats) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (user.loading || !user.loggedIn) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Table header style (white background for sticky headers, no gray)
  const tableHeaderStyle = {
    '& .MuiTableCell-head': {
      backgroundColor: '#ffffff',
      fontWeight: 600
    }
  };

  return (
    <Container maxWidth="xl" sx={{ overflowX: 'hidden' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={handleRefresh}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Platform Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of platform performance and merchant activity
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={handleRefresh}
          variant="outlined"
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {/* Overview Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Stamps */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6' sx={{ flexGrow: 1 }}>Total Stamps</Typography>
                <LocalCafe color="primary" sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant='h4' color="primary" sx={{ mb: 2 }}>
                {overviewStats?.totalStamps.toLocaleString() || 0}
              </Typography>
              {overviewStats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'primary.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Per Day</Typography>
                      <Typography variant='h6'>{overviewStats.stampsPerDay.current}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    {(() => {
                      const change = formatChangePercent(overviewStats.stampsPerDay.changePercent);
                      return (
                        <Box sx={{ border: '4px solid', borderColor: !change.isValid ? 'grey.400' : change.isPositive ? 'success.main' : 'error.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                          <Typography variant='subtitle2'>Change</Typography>
                          <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {change.display}
                            {change.isValid && (change.isPositive ? 
                              <TrendingUp sx={{ ml: 0.5, color: 'success.main' }} /> : 
                              <TrendingDown sx={{ ml: 0.5, color: 'error.main' }} />
                            )}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                2 week daily average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6' sx={{ flexGrow: 1 }}>Total Users</Typography>
                <People color="secondary" sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant='h4' color="secondary" sx={{ mb: 2 }}>
                {overviewStats?.totalUsers.toLocaleString() || 0}
              </Typography>
              {overviewStats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'secondary.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Per Day</Typography>
                      <Typography variant='h6'>{overviewStats.signupsPerDay.current}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    {(() => {
                      const change = formatChangePercent(overviewStats.signupsPerDay.changePercent);
                      return (
                        <Box sx={{ border: '4px solid', borderColor: !change.isValid ? 'grey.400' : change.isPositive ? 'success.main' : 'error.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                          <Typography variant='subtitle2'>Change</Typography>
                          <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {change.display}
                            {change.isValid && (change.isPositive ? 
                              <TrendingUp sx={{ ml: 0.5, color: 'success.main' }} /> : 
                              <TrendingDown sx={{ ml: 0.5, color: 'error.main' }} />
                            )}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                2 week daily average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Rewards Issued */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6' sx={{ flexGrow: 1 }}>Rewards Issued</Typography>
                <CardGiftcard color="warning" sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant='h4' color="warning.main" sx={{ mb: 2 }}>
                {overviewStats?.rewards.totalIssued.toLocaleString() || 0}
              </Typography>
              {overviewStats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'primary.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Total</Typography>
                      <Typography variant='h6'>{overviewStats.rewards.totalIssuedByTrigger.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'error.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Streak</Typography>
                      <Typography variant='h6'>{overviewStats.rewards.streakIssuedByTrigger.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Rewards Redeemed */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6' sx={{ flexGrow: 1 }}>Rewards Redeemed</Typography>
                <CardGiftcard color="success" sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant='h4' color="success.main" sx={{ mb: 2 }}>
                {overviewStats?.rewards.totalRedeemed.toLocaleString() || 0}
              </Typography>
              {overviewStats && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'primary.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Total</Typography>
                      <Typography variant='h6'>{overviewStats.rewards.totalRedeemedByTrigger.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ border: '4px solid', borderColor: 'error.main', p: 1, borderRadius: 3, textAlign: 'center' }}>
                      <Typography variant='subtitle2'>Streak</Typography>
                      <Typography variant='h6'>{overviewStats.rewards.streakRedeemedByTrigger.toLocaleString()}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Daily Stamps Chart */}
      <Accordion 
        expanded={expandedSections.dailyStamps} 
        onChange={() => toggleSection('dailyStamps')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Daily Stamps</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={timeFilter}
                  label="Time Period"
                  onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="this_year">This Year</MenuItem>
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {timeFilter === 'custom' && (
                <>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button variant="contained" size="small" onClick={handleCustomDateSearch}>
                    Apply
                  </Button>
                </>
              )}
            </Stack>
          </Box>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={dailyStamps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(value) => moment(value).format('MMM DD')} />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => moment(value).format('MMM DD, YYYY')}
                formatter={(value: number) => [value.toLocaleString(), 'Stamps']}
              />
              <Legend />
              <Bar dataKey="count" name="Stamps" fill="#0066CC" />
            </BarChart>
          </ResponsiveContainer>
        </AccordionDetails>
      </Accordion>

      {/* Stamps by Store Chart */}
      <Accordion 
        expanded={expandedSections.stampsByStore} 
        onChange={() => toggleSection('stampsByStore')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Stamps by Store</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={storeTimeFilter}
                  label="Time Period"
                  onChange={(e) => setStoreTimeFilter(e.target.value as TimeFilter)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="this_year">This Year</MenuItem>
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
              {storeTimeFilter === 'custom' && (
                <>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={storeCustomStartDate}
                    onChange={(e) => setStoreCustomStartDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={storeCustomEndDate}
                    onChange={(e) => setStoreCustomEndDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button variant="contained" size="small" onClick={handleStoreCustomDateSearch}>
                    Apply
                  </Button>
                </>
              )}
            </Stack>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Top 20 Stores by Stamps {storeTimeFilter !== 'all_time' && `(${storeTimeFilter.replace('_', ' ')})`}
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'hidden' }}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={stampsByStore.slice(0, 20)} margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="merchantName" 
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#081D33" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* User Base Growth Chart */}
      <Accordion 
        expanded={expandedSections.userGrowth} 
        onChange={() => toggleSection('userGrowth')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">User Base Growth</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={userGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={(value) => moment(value).format('MMM YYYY')} />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => moment(value).format('MMM DD, YYYY')}
                formatter={(value: number) => [value.toLocaleString(), 'Total Users']}
              />
              <Legend />
              <Line type="monotone" dataKey="totalUsers" name="Total Users" stroke="#081D33" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </AccordionDetails>
      </Accordion>

      {/* Activity Map */}
      <Accordion 
        expanded={expandedSections.merchantMap} 
        onChange={() => toggleSection('merchantMap')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Activity Map</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <MerchantMap accessToken={import.meta.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''} height={500} />
        </AccordionDetails>
      </Accordion>


      {/* Inactive Merchants */}
      <Accordion 
        expanded={expandedSections.inactiveMerchants} 
        onChange={() => toggleSection('inactiveMerchants')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Inactive Merchants
            <Chip label={filteredInactiveMerchants.length} size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Filtered on "Active" and "Free Trial" Subscriptions
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                <FilterList color="action" />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Inactivity Bucket</InputLabel>
                  <Select
                    multiple
                    value={inactivityFilter}
                    onChange={(e) => setInactivityFilter(e.target.value as string[])}
                    input={<OutlinedInput label="Inactivity Bucket" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {uniqueInactivityBuckets.map((bucket) => (
                      <MenuItem key={bucket} value={bucket}>
                        <Checkbox checked={inactivityFilter.includes(bucket)} />
                        <ListItemText primary={bucket} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Subscription Status</InputLabel>
                  <Select
                    multiple
                    value={subscriptionStatusFilter}
                    onChange={(e) => setSubscriptionStatusFilter(e.target.value as string[])}
                    input={<OutlinedInput label="Subscription Status" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {uniqueSubscriptionStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox checked={subscriptionStatusFilter.includes(status)} />
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(inactivityFilter.length > 0 || subscriptionStatusFilter.length > 0) && (
                  <Button
                    startIcon={<Clear />}
                    size="small"
                    onClick={() => {
                      setInactivityFilter([]);
                      setSubscriptionStatusFilter([]);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead sx={tableHeaderStyle}>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Billing Date</TableCell>
                      <TableCell>Last Purchase</TableCell>
                      <TableCell align="right">Total Purchases</TableCell>
                      <TableCell align="right">Days Inactive</TableCell>
                      <TableCell>Bucket</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInactiveMerchants.map((merchant) => (
                      <TableRow key={merchant.id} hover>
                        <TableCell>{merchant.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={merchant.subscriptionStatus}
                            size="small"
                            color={merchant.subscriptionStatus === 'ACTIVE' ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell>
                          {merchant.billingDate ? moment(merchant.billingDate).format('MMM DD, YYYY') : '-'}
                        </TableCell>
                        <TableCell>
                          {merchant.lastPurchaseDate 
                            ? moment(merchant.lastPurchaseDate).format('MMM DD, YYYY h:mm A')
                            : 'Never'}
                        </TableCell>
                        <TableCell align="right">{merchant.totalPurchases.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {merchant.daysInactive === 999 ? 'N/A' : merchant.daysInactive}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={merchant.inactivityBucket}
                            size="small"
                            sx={{
                              backgroundColor: INACTIVITY_COLORS[merchant.inactivityBucket] || '#9E9E9E',
                              color: 'white'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Inactive Merchants by Category
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={inactivityBucketCounts}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {inactivityBucketCounts.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={INACTIVITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>


      {/* Recent Activity - Two cards side by side, no nested cards */}
      <Accordion 
        expanded={expandedSections.recentActivity} 
        onChange={() => toggleSection('recentActivity')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Recent Activity</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Today's Stamps */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>Today's Stamps</Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead sx={tableHeaderStyle}>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Merchant</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentStamps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No stamps today</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentStamps.map((stamp) => (
                        <TableRow key={stamp.id} hover>
                          <TableCell>{stamp.id}</TableCell>
                          <TableCell>{stamp.userFirstName} {stamp.userLastName}</TableCell>
                          <TableCell>{stamp.merchantName}</TableCell>
                          <TableCell>{moment(stamp.dateCreated).format('h:mm A')}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Recent Rewards */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>Recent Rewards</Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead sx={tableHeaderStyle}>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Merchant</TableCell>
                      <TableCell>Redeemed</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRewards.map((reward) => (
                      <TableRow key={reward.id} hover>
                        <TableCell>{reward.id}</TableCell>
                        <TableCell>{reward.userFirstName} {reward.userLastName}</TableCell>
                        <TableCell>{reward.merchantName}</TableCell>
                        <TableCell>
                          <Chip
                            label={reward.redeemed ? 'Yes' : 'No'}
                            size="small"
                            color={reward.redeemed ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={reward.triggerType === 'TOTAL_TARGET' ? 'TOTAL' : 'STREAK'}
                            size="small"
                            color={reward.triggerType === 'TOTAL_TARGET' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>{moment(reward.dateCreated).format('MMM DD, YYYY')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>


      {/* Merchants and Subscriptions Table - Last */}
      <Accordion 
        expanded={expandedSections.merchantsTable} 
        onChange={() => toggleSection('merchantsTable')}
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Merchants and Subscriptions
            <Chip label={filteredAndSortedMerchants.length} size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Filters */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <FilterList color="action" />
            <TextField
              size="small"
              placeholder="Search by name or token..."
              value={merchantSearchToken}
              onChange={(e) => setMerchantSearchToken(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Subscription Status</InputLabel>
              <Select
                multiple
                value={merchantStatusFilter}
                onChange={(e) => setMerchantStatusFilter(e.target.value as string[])}
                input={<OutlinedInput label="Subscription Status" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {uniqueMerchantStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={merchantStatusFilter.includes(status)} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                multiple
                value={merchantTypeFilter}
                onChange={(e) => setMerchantTypeFilter(e.target.value as string[])}
                input={<OutlinedInput label="Type" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {uniqueMerchantTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={merchantTypeFilter.includes(type)} />
                    <ListItemText primary={type} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(merchantSearchToken || merchantStatusFilter.length > 0 || merchantTypeFilter.length > 0) && (
              <Button
                startIcon={<Clear />}
                size="small"
                onClick={() => {
                  setMerchantSearchToken('');
                  setMerchantStatusFilter([]);
                  setMerchantTypeFilter([]);
                }}
              >
                Clear
              </Button>
            )}
          </Stack>

          <Box sx={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead sx={tableHeaderStyle}>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'id'}
                        direction={merchantSortField === 'id' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Logo</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'name'}
                        direction={merchantSortField === 'name' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'type'}
                        direction={merchantSortField === 'type' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'subscriptionStatus'}
                        direction={merchantSortField === 'subscriptionStatus' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('subscriptionStatus')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'subscriptionToken'}
                        direction={merchantSortField === 'subscriptionToken' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('subscriptionToken')}
                      >
                        Subscription Token
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'billingDate'}
                        direction={merchantSortField === 'billingDate' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('billingDate')}
                      >
                        Billing Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'dateCreated'}
                        direction={merchantSortField === 'dateCreated' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('dateCreated')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={merchantSortField === 'dateUpdated'}
                        direction={merchantSortField === 'dateUpdated' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('dateUpdated')}
                      >
                        Updated
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Referral Code</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={merchantSortField === 'totalStamps'}
                        direction={merchantSortField === 'totalStamps' ? merchantSortOrder : 'asc'}
                        onClick={() => handleMerchantSort('totalStamps')}
                      >
                        Total Stamps
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedMerchants
                    .slice(merchantsPage * merchantsRowsPerPage, merchantsPage * merchantsRowsPerPage + merchantsRowsPerPage)
                    .map((merchant) => (
                    <TableRow key={merchant.id} hover>
                      <TableCell>{merchant.id}</TableCell>
                      <TableCell>
                        <Avatar src={merchant.logoUrl} alt={merchant.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>{merchant.name}</TableCell>
                      <TableCell>{merchant.location || '-'}</TableCell>
                      <TableCell>{merchant.type}</TableCell>
                      <TableCell>
                        {merchant.subscriptionStatus ? (
                          <Chip
                            label={merchant.subscriptionStatus}
                            size="small"
                            color={
                              merchant.subscriptionStatus === 'ACTIVE' ? 'success' :
                              merchant.subscriptionStatus === 'FREE_TRIAL' ? 'info' :
                              merchant.subscriptionStatus === 'CANCELLED' ? 'error' :
                              'default'
                            }
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {merchant.subscriptionToken || '-'}
                      </TableCell>
                      <TableCell>
                        {merchant.billingDate ? moment(merchant.billingDate).format('MMM DD, YYYY') : '-'}
                      </TableCell>
                      <TableCell>
                        {merchant.dateCreated ? moment(merchant.dateCreated).format('MMM DD, YYYY') : '-'}
                      </TableCell>
                      <TableCell>
                        {moment(merchant.dateUpdated).format('MMM DD, YYYY')}
                      </TableCell>
                      <TableCell>{merchant.referralCode || '-'}</TableCell>
                      <TableCell align="right">{merchant.totalStamps.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAndSortedMerchants.length}
              page={merchantsPage}
              onPageChange={(_, newPage) => setMerchantsPage(newPage)}
              rowsPerPage={merchantsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setMerchantsRowsPerPage(parseInt(e.target.value, 10));
                setMerchantsPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default StatsAdminPage;
